/**
 * Microsoft 365 integration page.
 *
 * One-click admin consent OAuth flow — customer clicks Connect, signs in to Microsoft,
 * clicks Accept on the permission screen, and they're done. No credentials to copy.
 *
 * After connecting, all Microsoft-powered features in Velaro (SharePoint transcript storage,
 * web scraper output to SharePoint, future OneDrive/Teams features) use this connection.
 */
import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import useApi from "@/hooks/useApi";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import useAdminFeatures from "@/hooks/useAdminFeatures";
import IntegrationActivityLog from "../IntegrationActivityLog";

interface M365Status {
  connected: boolean;
  tenantId?: string;
  connectedAt?: string;
  connectedByEmail?: string;
  isEnabled?: boolean;
  sharePointSiteUrl?: string;
}

interface SharePointSite {
  id: string;
  displayName: string;
  webUrl: string;
}

export default function Microsoft365() {
  const api = useApi();
  const [searchParams] = useSearchParams();
  const { enableOneDriveAttachments, enableTeamsNotifications } = useAdminFeatures();

  const [status, setStatus] = useState<M365Status>({ connected: false });
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState<SharePointSite[]>([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [testSiteUrl, setTestSiteUrl] = useState("");
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connectError2, setConnectError2] = useState<string | null>(null);
  const [scopedSiteUrl, setScopedSiteUrl] = useState("");

  const justConnected = searchParams.get("connected") === "true";
  const connectError  = searchParams.get("error");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await api.admin.get("MicrosoftGraph/Status");
      const data = await res.json();
      setStatus(data);
    } catch {
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  async function loadSites() {
    setLoadingSites(true);
    try {
      const res  = await api.admin.get("MicrosoftGraph/Sites");
      const data = await res.json();
      setSites(Array.isArray(data) ? data : []);
    } catch {
      setSites([]);
    } finally {
      setLoadingSites(false);
    }
  }

  async function testConnection() {
    setTestResult(null);
    try {
      const res  = await api.admin.post("MicrosoftGraph/Test", { siteUrl: testSiteUrl || null });
      const data = await res.json();
      setTestResult(data);
    } catch {
      setTestResult({ success: false, message: "Test failed." });
    }
  }

  async function handleConnect() {
    setConnecting(true);
    setConnectError2(null);
    try {
      const params = scopedSiteUrl.trim()
        ? `?siteUrl=${encodeURIComponent(scopedSiteUrl.trim())}`
        : "";
      const resp = await api.admin.get(`MicrosoftGraph/ConnectUrl${params}`);
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({})) as { error?: string };
        setConnectError2(body?.error ?? "Failed to start Microsoft 365 authorization.");
        return;
      }
      const data = await resp.json() as { authUrl: string };
      window.location.href = data.authUrl;
    } catch {
      setConnectError2("Failed to start Microsoft 365 authorization. Please try again.");
    } finally {
      setConnecting(false);
    }
  }

  async function disconnect() {
    if (!confirm("Disconnect Microsoft 365? This will stop all SharePoint transcript pushes and any other Microsoft-connected features.")) return;
    setDisconnecting(true);
    try {
      await api.admin.delete("MicrosoftGraph");
      setStatus({ connected: false });
      setSites([]);
    } catch {
      /* ignore */
    } finally {
      setDisconnecting(false);
    }
  }

  if (loading) return <div className="p-8 text-sm text-slate-400">Loading...</div>;

  return (
    <div className="p-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <img src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg" alt="Microsoft" className="h-6 w-6" />
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Microsoft 365</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Connect your organization's Microsoft 365 to enable SharePoint file storage, OneDrive access, and other Microsoft-powered features.
          </p>
        </div>
      </div>

      {/* Success / error banners */}
      {justConnected && (
        <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800 mb-5">
          ✓ Microsoft 365 connected successfully! You can now use SharePoint transcript storage and other Microsoft features.
        </div>
      )}
      {connectError && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800 mb-5">
          Connection failed: {connectError === "access_denied" ? "Admin consent was declined." : connectError}
          {" "}<Link to="/integrations/microsoft-365" className="underline">Try again</Link>
        </div>
      )}

      {/* Connection card */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 mb-6">
        {status.connected ? (
          <>
            <div className="flex items-center gap-3 mb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100">
                <span className="text-green-600 text-sm">✓</span>
              </span>
              <div>
                <p className="text-sm font-semibold text-slate-800">Connected to Microsoft 365</p>
                <p className="text-xs text-slate-400">
                  Tenant: <code className="bg-slate-100 px-1 rounded">{status.tenantId}</code>
                  {status.connectedAt && ` · Connected ${new Date(status.connectedAt).toLocaleDateString()}`}
                </p>
                {status.sharePointSiteUrl && (
                  <p className="text-xs text-green-700 mt-0.5">
                    Scoped to: <code className="bg-green-50 px-1 rounded">{status.sharePointSiteUrl}</code>
                  </p>
                )}
              </div>
            </div>

            <p className="text-sm text-slate-600 mb-5">
              Velaro can now access your organization's SharePoint sites and OneDrive. All Microsoft-powered features across the platform will use this connection.
            </p>

            {/* Test connection */}
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 mb-4">
              <p className="text-sm font-medium text-slate-700 mb-2">Test a SharePoint site (optional)</p>
              <div className="flex gap-2">
                <input
                  className="flex-1 border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="https://contoso.sharepoint.com/sites/compliance"
                  value={testSiteUrl}
                  onChange={e => setTestSiteUrl(e.target.value)}
                />
                <SecondaryBlueButton label="Test" onClick={testConnection} />
              </div>
              {testResult && (
                <p className={`mt-2 text-xs ${testResult.success ? "text-green-700" : "text-red-600"}`}>
                  {testResult.success ? "✓ " : "✗ "}{testResult.message}
                </p>
              )}
            </div>

            {/* Site browser */}
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:underline"
                onClick={loadSites}
                disabled={loadingSites}
              >
                {loadingSites ? "Loading sites..." : "Browse SharePoint sites in your organization"}
              </button>
              {sites.length > 0 && (
                <div className="mt-3 rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-48 overflow-y-auto">
                  {sites.map(s => (
                    <div key={s.id} className="px-3 py-2 text-sm flex items-center justify-between">
                      <span className="text-slate-700">{s.displayName || s.webUrl}</span>
                      <button
                        className="text-xs text-blue-600 hover:underline ml-2"
                        onClick={() => { setTestSiteUrl(s.webUrl); }}
                      >
                        Copy URL
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <SecondaryBlueButton
              label={disconnecting ? "Disconnecting..." : "Disconnect Microsoft 365"}
              onClick={disconnect}
              disabled={disconnecting}
            />
          </>
        ) : (
          <>
            <p className="text-sm text-slate-700 mb-4">
              Click <strong>Connect Microsoft 365</strong> to authorize Velaro to access your organization's SharePoint and OneDrive.
              You'll be redirected to Microsoft's secure consent page — just sign in as an M365 admin and click <strong>Accept</strong>.
            </p>

            {/* Optional: scope to a specific SharePoint site */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                SharePoint site to connect <span className="text-slate-400 font-normal">(recommended)</span>
              </label>
              <input
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="https://yourcompany.sharepoint.com/sites/velaro"
                value={scopedSiteUrl}
                onChange={e => setScopedSiteUrl(e.target.value)}
              />
              <p className="text-xs text-slate-400 mt-1">
                When provided, Velaro's access is scoped exclusively to this site. Leave blank to grant access to all SharePoint sites in your tenant.
              </p>
            </div>

            <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-800 mb-5 space-y-1">
              <p className="font-medium">Permissions requested</p>
              <ul className="list-disc list-inside text-xs space-y-0.5 mt-1">
                <li><strong>SharePoint</strong> — read/write files in the site you specify (scoped to that site only if you enter a URL above)</li>
                <li><strong>Calendars.ReadWrite</strong> — book appointments in any M365 user or group calendar you designate</li>
                <li><strong>Bookings</strong> — read and manage Microsoft Bookings appointments and services</li>
                <li>Velaro <strong>never</strong> gets your password or admin credentials</li>
                <li>Revoke anytime: Azure Active Directory → Enterprise applications → Velaro → Delete</li>
              </ul>
            </div>

            {connectError2 && (
              <p className="text-sm text-red-600 mb-3">{connectError2}</p>
            )}
            <PrimaryBlueButton
              label={connecting ? "Redirecting to Microsoft…" : "Connect Microsoft 365"}
              onClick={handleConnect}
              disabled={connecting}
            />
          </>
        )}
      </div>

      {/* How it works */}
      <HowItWorks />

      {/* What it unlocks */}
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <p className="text-sm font-semibold text-slate-700 mb-3">Microsoft-powered features</p>
        <div className="space-y-4">
          {/* SharePoint Transcript Storage */}
          <FeatureRow
            title="SharePoint Transcript Storage"
            desc="Push conversation transcripts to any SharePoint document library after resolve."
            badge="Live"
            ready={status.connected}
            link="/integrations/transcript-storage"
            linkLabel="Configure →"
          />

          {/* Web Scraper → SharePoint */}
          <FeatureRow
            title="Web Scraper → SharePoint Export"
            desc="Save web scraping results directly to a SharePoint document library for team collaboration and compliance."
            badge="Live"
            ready={status.connected}
          />

          {/* Outlook Calendar */}
          <FeatureRow
            title="Outlook Calendar Booking"
            desc="Let visitors book appointments in chat. Works with any M365 user mailbox or group/shared calendar — enter the calendar's email address as the organizer."
            badge="Live"
            ready={status.connected}
            link="/integrations/outlook-calendar"
            linkLabel="Configure →"
          />

          {/* Microsoft Bookings */}
          <FeatureRow
            title="Microsoft Bookings"
            desc="AI bot books appointments using your Bookings business pages — check availability, create bookings, manage services."
            badge="Live"
            ready={status.connected}
            link="/integrations/microsoft-bookings"
            linkLabel="Configure →"
          />

          {/* OneDrive File Attachments */}
          <FeatureRow
            title="OneDrive File Attachments"
            desc="Agents can browse a shared file library and share links directly in chat conversations."
            badge={enableOneDriveAttachments ? "Live" : "Upgrade"}
            ready={status.connected && enableOneDriveAttachments}
            link={enableOneDriveAttachments ? "/integrations/microsoft-365/onedrive" : undefined}
            linkLabel="Configure →"
            locked={!enableOneDriveAttachments}
          />

          {/* Teams Notifications */}
          <FeatureRow
            title="Teams Channel Notifications"
            desc="Send alerts to Teams channels when conversations arrive, resolve, are missed, or breach SLA. Uses Teams webhooks — no Graph permissions required."
            badge={enableTeamsNotifications ? "Live" : "Upgrade"}
            ready={enableTeamsNotifications}
            link={enableTeamsNotifications ? "/integrations/microsoft-365/teams-notifications" : undefined}
            linkLabel="Configure →"
            locked={!enableTeamsNotifications}
          />
        </div>
      </div>
    </div>
  );
}

// ── Feature row component ─────────────────────────────────────────────────────

function FeatureRow({
  title, desc, badge, ready, link, linkLabel, locked
}: {
  title: string;
  desc: string;
  badge: "Live" | "Upgrade" | "Soon";
  ready?: boolean;
  link?: string;
  linkLabel?: string;
  locked?: boolean;
}) {
  const badgeClass =
    badge === "Live"    ? "bg-green-100 text-green-700" :
    badge === "Upgrade" ? "bg-amber-100 text-amber-700" :
                          "bg-slate-100 text-slate-400";

  return (
    <div className="flex items-start gap-3">
      <span className={`flex-shrink-0 mt-0.5 text-xs font-medium px-1.5 py-0.5 rounded ${badgeClass}`}>
        {badge}
      </span>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-slate-700">{title}</p>
          {ready === true && (
            <span className="text-xs text-green-600 font-medium">✓ Ready</span>
          )}
          {ready === false && badge === "Live" && !locked && (
            <span className="text-xs text-slate-400">Connect M365 above to enable</span>
          )}
        </div>
        <p className="text-xs text-slate-400">{desc}</p>
        {link && linkLabel && !locked && ready && (
          <Link to={link} className="text-xs text-blue-600 hover:underline mt-0.5 inline-block">
            {linkLabel}
          </Link>
        )}
        {locked && (
          <p className="text-xs text-amber-600 mt-0.5">Contact support to enable this feature.</p>
        )}
      </div>
    </div>
  );
}

// ── How it works (expandable) ─────────────────────────────────────────────────

function HowItWorks() {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-xl border border-slate-200 bg-white mb-6 overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-6 py-4 text-sm font-medium text-slate-700 hover:bg-slate-50 text-left"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
      >
        <span>How does this connection work?</span>
        <span className="text-slate-400 text-xs ml-2">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="px-6 pb-6 border-t border-slate-100 space-y-5 text-sm text-slate-600">

          {/* The key insight */}
          <div className="mt-4 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3">
            <p className="font-semibold text-blue-900 mb-1">You store nothing. Velaro handles the credentials.</p>
            <p className="text-blue-800 text-xs leading-relaxed">
              Velaro has one Microsoft Azure app (registered by Velaro, maintained by Velaro).
              When you click Connect, you're authorizing that Velaro app to access <em>your</em> Microsoft 365 tenant.
              Microsoft gives Velaro a one-time authorization record — your tenant ID.
              After that, Velaro uses its own credentials + your tenant ID to call Microsoft on your behalf.
              Your admin credentials are never stored anywhere.
            </p>
          </div>

          {/* Step by step */}
          <div>
            <p className="font-semibold text-slate-700 mb-3">What happens when you click Connect</p>
            <ol className="space-y-3">
              {[
                {
                  n: "1",
                  title: "You're redirected to Microsoft",
                  body: "Velaro sends you to Microsoft's secure admin consent page. You sign in with your Microsoft 365 admin account — Velaro never sees this password."
                },
                {
                  n: "2",
                  title: "You review and accept",
                  body: "Microsoft shows you exactly what Velaro is requesting: read/write access to SharePoint sites you designate. You click Accept. If you click Decline, nothing is stored."
                },
                {
                  n: "3",
                  title: "Microsoft sends Velaro your tenant ID",
                  body: "Microsoft redirects you back to Velaro's callback URL with your organization's tenant ID (a GUID like 4a3b1c...). This is not a secret — it's just your organization's unique Microsoft ID."
                },
                {
                  n: "4",
                  title: "Velaro stores the tenant ID",
                  body: "Velaro stores your tenant ID in its database. This is the only thing stored on Velaro's side related to your Microsoft connection."
                },
                {
                  n: "5",
                  title: "Future calls use Velaro's app + your tenant ID",
                  body: "When Velaro needs to push a transcript to SharePoint or list your sites, it combines its own Client ID + Client Secret (in Velaro's server config) with your tenant ID to get a short-lived access token from Microsoft. The token expires after ~1 hour — Velaro requests a fresh one each time."
                }
              ].map(step => (
                <li key={step.n} className="flex gap-3">
                  <span className="flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold mt-0.5">
                    {step.n}
                  </span>
                  <div>
                    <p className="font-medium text-slate-700">{step.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{step.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* Two paths diagram */}
          <div>
            <p className="font-semibold text-slate-700 mb-3">SharePoint storage: two connection options</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                <p className="text-xs font-bold text-green-700 uppercase tracking-wide mb-2">Option A — Microsoft 365 connected (recommended)</p>
                <p className="text-xs text-slate-700 leading-relaxed mb-2">
                  Connect once here. Then in <strong>Transcript Storage</strong>, choose SharePoint and just enter your site URL. No credentials to copy.
                </p>
                <p className="text-xs text-slate-500">
                  Uses Velaro's app + your tenant consent.<br />
                  <em>Customer stores: nothing.</em>
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Option B — Your own Azure app</p>
                <p className="text-xs text-slate-700 leading-relaxed mb-2">
                  Register your own app in Azure AD and provide the Tenant ID, Client ID, and Client Secret in the Transcript Storage config form.
                </p>
                <p className="text-xs text-slate-500">
                  You control the app registration.<br />
                  <em>Customer stores: 3 credentials per config.</em>
                </p>
              </div>
            </div>
          </div>

          {/* Revoke */}
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-800">
            <p className="font-semibold mb-1">How to revoke access</p>
            <p>
              Click <strong>Disconnect Microsoft 365</strong> on this page — Velaro deletes your tenant ID from its database.
              To fully remove Velaro from your Microsoft tenant, go to{" "}
              <strong>Azure Active Directory → Enterprise applications → Velaro → Delete</strong>.
              Velaro has no way to access your tenant after you do either of these.
            </p>
          </div>

        </div>
      )}

      {/* Activity Log */}
      <div className="mt-8">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Activity Log</h2>
        <IntegrationActivityLog integrationSlug="Microsoft365" />
      </div>
    </div>
  );
}
